/**
 * Errors, with the design attached.
 *
 * A crash that arrives carrying the spec that caused it is not a support ticket, it
 * is a **native test case**: the spec drops into `engine/tests/corpus`, reproduces
 * in the fast native loop, and is fixed with a debugger rather than by reading a
 * stack trace from a stranger's phone (CLAUDE.md, "Workflow — native first"). That
 * is worth more during alpha than the error count is, and it is the reason the spec
 * rides along on every report.
 *
 * The spec is safe to send: it describes a shed, not a person (DESIGN §10). Nothing
 * else is sent — `sendDefaultPii` stays off, and there is no user identification of
 * any kind.
 *
 * With no DSN configured, Sentry never initializes and every function here is inert.
 * That is the zero-standing-infrastructure default: a checkout with no environment
 * set up reports nowhere and costs nothing.
 */

import type { Spec } from "@planforge/wasm";
import * as Sentry from "@sentry/react";

export function startErrorReporting(): void {
  const dsn = import.meta.env.VITE_SENTRY_DSN;
  if (dsn === undefined || dsn === "") {
    return;
  }

  Sentry.init({
    dsn,
    // No performance tracing and no session replay: this is a crash reporter during
    // alpha, and the funnel is the emitter's job, not Sentry's.
    tracesSampleRate: 0,
    sendDefaultPii: false,
    // Source maps are emitted by the build and served beside the bundle, so a stack
    // trace resolves to real lines with no upload step to keep running.
    release: __APP_VERSION__,
  });
}

/**
 * Attach the current design to whatever fails next.
 *
 * Set once per settled design change rather than caught per call site, so an error
 * thrown anywhere — a render, an effect, a handler nobody wrapped — still arrives
 * with the design that produced it.
 */
export function setSpecContext(spec: Spec): void {
  Sentry.setContext("spec", spec as unknown as Record<string, unknown>);
}

/** Report an error we caught ourselves, with the design in hand. */
export function captureDesignError(error: unknown, spec: Spec | undefined): void {
  if (spec !== undefined) {
    setSpecContext(spec);
  }
  Sentry.captureException(error);
}
